import { CloseIcon, PlusIcon } from 'components/icons'
import { useEffect, useRef, useState } from 'react'
import Button from '../button'
import { TextInput } from '../input'
import { FormSetValue } from 'types/input'
import { FieldErrors, FieldValues, Path, PathValue } from 'react-hook-form'

type Props<T extends FieldValues> = {
  label: string
  placeholder: string
  defaultValues?: string[]
  errors?: FieldErrors<T>
} & FormSetValue<T>

/**
 * DynamicInputList component
 *
 * This component will render a list of inputs and
 * will allow the user to add or remove the input from the list.
 */
const DynamicInputList = <T extends FieldValues>({
  label,
  placeholder,
  setValue,
  name,
  defaultValues,
  errors,
}: Props<T>) => {
  const [inputList, setInputList] = useState<string[]>([])

  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (defaultValues) {
      setInputList(defaultValues)
    }
  }, [defaultValues])

  // Will add the input value to the list
  const submitInput = () => {
    if (inputRef?.current?.value) {
      const value = [...inputList, inputRef?.current?.value]
      setInputList(value)
      inputRef.current.value = ''
      setValue(name, value as PathValue<T, string & Path<T>>, {
        shouldValidate: true,
      })
    }
  }

  // Will remove the selected input from the list
  const deleteInput = (index: number) => {
    const value = inputList
    value.splice(index, 1)
    setInputList([...value])
    setValue(name, value as PathValue<T, string & Path<T>>)
  }

  return (
    <>
      <div className="flex items-start gap-x-3">
        <TextInput ref={inputRef} label={label} placeholder={placeholder} />
        <Button
          type="button"
          className="mt-8"
          icon={<PlusIcon />}
          onClick={submitInput}
        />
      </div>
      <p
        className={`${
          errors?.[name]?.message ? 'opacity-100' : 'opacity-0'
        } text-xs text-red-600`}
      >
        {errors?.[name]?.message as string}
      </p>
      {inputList.length > 0 && (
        <div className="mt-1 flex w-full flex-col divide-y rounded-md border">
          {inputList.map((list, i) => (
            <div
              key={list + i}
              className="flex items-start justify-between gap-x-2 px-2 py-2 hover:bg-gray-50"
            >
              <p>{list}</p>
              <Button
                type="button"
                variant="custom"
                className="mt-1"
                size="small"
                iconClassName="!size-3 text-gray-400 hover:text-red-400"
                icon={<CloseIcon />}
                onClick={() => deleteInput(i)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export default DynamicInputList
